import { useRef, useState, useEffect } from "react";
import { usePymbleApi } from "./usePymbleApi";

const DEBOUNCE_MS = 350;

const SEARCH_ENDPOINT = "search";

/** A staff search result from the federated /search endpoint (scope=staff). */
export interface StaffSearchResult {
	/** ContactPersonal._id. */
	id: string;
	scope: "staff";
	/** Full name. */
	title: string;
	/** "Title | Department". */
	subtitle: string;
	email?: string;
}

/**
 * Search staff via the federated /search endpoint, scoped to the staff contact
 * scope. Ported from my-pymble-portal's useStaffSearch.
 *
 * /search requires a query of at least 2 chars; shorter queries clear results.
 */
export function useStaffSearch() {
	const { callApi } = usePymbleApi();
	const [results, setResults] = useState<StaffSearchResult[]>([]);
	const [isSearching, setIsSearching] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Monotonic request id — only the latest request is allowed to set results,
	// so slower out-of-order responses can't overwrite newer ones.
	const seqRef = useRef(0);

	// Clean up a pending debounce on unmount.
	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	const runSearch = async (q: string, seq: number) => {
		setIsSearching(true);
		try {
			// /search groups results by scope: { results: { staff: [...] }, ... }
			const res: { results?: { staff?: StaffSearchResult[] } } = await callApi(
				"GET",
				`${SEARCH_ENDPOINT}?q=${encodeURIComponent(q)}&scopes=staff&limit=25`
			);
			if (seq !== seqRef.current) return; // a newer search superseded this one
			setResults(res?.results?.staff ?? []);
		} catch {
			if (seq === seqRef.current) setResults([]);
		} finally {
			if (seq === seqRef.current) setIsSearching(false);
		}
	};

	/** Debounced staff search. Fires ~350ms after the last keystroke; only the
	 *  latest in-flight request is applied. */
	const search = (query: string) => {
		const q = (query ?? "").trim();
		if (debounceRef.current) clearTimeout(debounceRef.current);
		if (q.length < 2) {
			seqRef.current += 1; // invalidate any in-flight request
			setResults([]);
			setIsSearching(false);
			return;
		}
		const seq = ++seqRef.current;
		debounceRef.current = setTimeout(() => runSearch(q, seq), DEBOUNCE_MS);
	};

	const clear = () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
		seqRef.current += 1;
		setResults([]);
	};

	return { results, isSearching, search, clear };
}
